import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Crown, Check, ArrowLeft, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "R35",
    bullets: [
      "Up to 3 active projects",
      "Standard text & image tools",
      "Instant subdomain publishing",
      "7-day free trial",
    ],
    accent: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "R50",
    bullets: [
      "Unlimited projects",
      "Full Canva-style template library",
      "Drag-and-drop uploads workspace",
      "Raw source ZIP exports",
      "Social media share gateway",
      "7-day free trial",
    ],
    accent: true,
  },
];

export default function Upgrade({ success, cancel }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (success) {
      toast.success("Subscription started — welcome aboard.");
      refresh();
    } else if (cancel) {
      toast.message("Subscription cancelled — you can try again anytime.");
    }
  }, [success, cancel, refresh]);

  const subscribe = async (tier) => {
    try {
      const { data } = await api.post("/subscribe", { tier });
      if (data.master) {
        toast.success("Master access — no payment required.");
        return;
      }
      // Build hidden form and submit to PayFast
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.url;
      Object.entries(data.payload).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
    }
  };

  const devActivate = async (tier) => {
    try {
      await api.post("/subscribe/dev-activate", { tier });
      await refresh();
      toast.success(`${tier.toUpperCase()} plan activated (sandbox).`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sub = user?.subscription || {};

  return (
    <div className="min-h-screen bg-[#E5E7EB]">
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
        <button
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#002FA7]"
          onClick={() => nav("/dashboard")}
          data-testid="upgrade-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#002FA7] flex items-center justify-center text-white">
            <Sparkles className="w-4 h-4" strokeWidth={1.6} />
          </div>
          <span className="font-display font-bold tracking-tight">CreativeHub Pro Builder</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Crown className="w-3.5 h-3.5" /> Upgrade plan
          </div>
          <h1 className="font-display text-5xl font-bold tracking-tighter">Pick the plan that ships your idea.</h1>
          <p className="text-base text-gray-600 mt-3 max-w-xl mx-auto">
            7 days free on any plan. PayFast handles billing securely in ZAR.
            {sub.is_master ? " You currently have Master Developer access — no payment required." : ""}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-2xl p-8 border ${
                plan.accent
                  ? "bg-[#0B1220] text-white border-transparent shadow-xl"
                  : "bg-white border-gray-200"
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-2xl font-bold tracking-tight">{plan.name}</h3>
                {plan.accent && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-gradient-to-r from-[#FF4500] to-[#FF8C00]">
                    Most popular
                  </span>
                )}
              </div>
              <div className="mt-6">
                <span className="font-display text-5xl font-bold tracking-tighter">{plan.price}</span>
                <span className={plan.accent ? "text-white/60 ml-1" : "text-gray-500 ml-1"}>/ month</span>
              </div>
              <ul className="mt-6 space-y-3">
                {plan.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 ${plan.accent ? "text-emerald-400" : "text-emerald-600"}`} />
                    <span className={plan.accent ? "text-white/85" : "text-gray-700"}>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 space-y-2">
                <Button
                  className={
                    plan.accent
                      ? "w-full bg-gradient-to-r from-[#FF4500] to-[#FF8C00] hover:opacity-90 text-white h-11 font-bold"
                      : "w-full bg-[#002FA7] hover:bg-blue-800 text-white h-11"
                  }
                  onClick={() => subscribe(plan.id)}
                  data-testid={`plan-subscribe-${plan.id}`}
                >
                  Start 7-day free trial via PayFast
                </Button>
                <button
                  className="text-xs text-gray-400 hover:text-gray-600 w-full text-center"
                  onClick={() => devActivate(plan.id)}
                  data-testid={`plan-dev-activate-${plan.id}`}
                >
                  Sandbox: instantly activate {plan.name} (dev only)
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-400 text-center mt-10 font-mono">
          You will be redirected to PayFast Sandbox. R0.00 today, billed after the 7-day trial.
        </p>
      </main>
    </div>
  );
}
